import { useEffect, useState } from 'react'
import { useGetUserCustomers } from 'src/api/customer'

/**
 * Reactively resolves the app's current customer id.
 *
 * Prefers the user's selected customer (localStorage.currentCustomerId, written
 * by the sidebars on click) and keeps it up to date via a short poll plus
 * storage/focus events. Falls back to the user's first customer when nothing has
 * been selected yet (e.g. a single-customer user who never clicked a header),
 * so customer-scoped UI (notification bell, preferences) works without a refresh.
 */
export const useCurrentCustomerId = (): number => {
  const read = () => Number(localStorage.getItem('currentCustomerId')) || 0
  const [selected, setSelected] = useState<number>(read)

  useEffect(() => {
    const update = () =>
      setSelected(prev => {
        const next = read()
        return next !== prev ? next : prev
      })
    const interval = setInterval(update, 1000)
    window.addEventListener('storage', update)
    window.addEventListener('focus', update)
    return () => {
      clearInterval(interval)
      window.removeEventListener('storage', update)
      window.removeEventListener('focus', update)
    }
  }, [])

  const { data: userCustomers } = useGetUserCustomers()
  const fallback = userCustomers?.items?.[0]?.id ?? 0

  return selected > 0 ? selected : fallback
}
